import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Toggle } from "../../components/Toggle";
import { PrimaryButton } from "../../components/ui";
import { UndoToast } from "../../components/crm/ui";
import { InlineLoader } from "../../components/PageLoader";
import { TextInput } from "../../components/crm/leadsKit";
import { fetchApi } from "../../components/marketing/access";
import { ConnectFrame, ConnectGate, ErrorNote, useIntegration } from "../../components/settings/integrationsKit";

/**
 * Settings → Integrations → :id → WhatsApp lead capture (design: IntegrationConnect.tsx, "whatsapp-business").
 *
 * Connect: POST /api/integrations/:id/connect-whatsapp { phone_number_id, business_account_id,
 * access_token?, ctwa_only, any_new_number, tag_ad_name }. The backend checks the number is in that
 * WhatsApp Business Account with Meta (the token given, else the clinic's own WhatsApp connection's
 * token), stores both ids, marks the integration connected and sets the capture rules.
 * The clinic's connected number (GET /api/waba/config) fills both ids, so a clinic that already set up
 * WhatsApp only presses the button; another number, or a separate system user token, is under
 * "Use a different number".
 * The three capture rules are saved as soon as they change (PUT /api/integrations/:id { <rule> }),
 * the same call the integration's settings page uses; they are also sent with the connect.
 * Leads arrive through the clinic's WhatsApp webhook, so there is no URL to paste anywhere.
 */

const RULES = [
  { key: "ctwa_only", label: "Create a lead when a chat starts from an ad", fallback: true },
  { key: "any_new_number", label: "Also create a lead for any new number that messages", fallback: false },
  { key: "tag_ad_name", label: "Tag leads with the ad name", fallback: true },
];
const parseRules = (v) => {
  if (!v) return {};
  if (typeof v !== "string") return v;
  try { return JSON.parse(v) || {}; } catch (_) { return {}; }
};
const rulesOf = (row) => {
  const saved = parseRules(row?.capture_rules);
  return Object.fromEntries(RULES.map((r) => [r.key, saved[r.key] === undefined ? r.fallback : !!saved[r.key]]));
};

export default function ConnectWhatsApp() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { row: integration, loading } = useIntegration(id);
  const [waba, setWaba] = useState(undefined); // undefined loading · null none · { … } the connected number
  const [wabaError, setWabaError] = useState("");
  const [rules, setRules] = useState(rulesOf(null));
  const [savingRule, setSavingRule] = useState("");
  const [other, setOther] = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [businessAccountId, setBusinessAccountId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchApi("/api/waba/config").then((r) => {
      if (!r.ok) { setWaba(null); setWabaError(r.message || "Couldn't load your WhatsApp number."); return; }
      const cfg = r.data?.config || null;
      setWaba(cfg);
      if (cfg) { setPhoneNumberId(String(cfg.phone_number_id || "")); setBusinessAccountId(String(cfg.business_account_id || "")); }
      else setOther(true);
    });
  }, []);
  useEffect(() => { if (integration) setRules(rulesOf(integration)); }, [integration]);
  // Old page: an integration that is already connected goes straight to its settings.
  useEffect(() => { if (integration?.is_connected) navigate(`/settings/integrations/${id}`, { replace: true }); }, [integration, id, navigate]);

  const setRule = async (key) => {
    const on = !rules[key];
    setRules((r) => ({ ...r, [key]: on }));
    setSavingRule(key);
    const r = await fetchApi(`/api/integrations/${id}`, { method: "put", body: { [key]: on } });
    setSavingRule("");
    if (!r.ok) { setRules((cur) => ({ ...cur, [key]: !on })); setToast({ message: r.message || "Couldn't save the rule" }); return; }
    setToast({ message: "Capture rule saved" });
  };

  const connect = async () => {
    const phone = phoneNumberId.trim();
    const account = businessAccountId.trim();
    if (!/^\d{5,25}$/.test(phone) || !/^\d{5,25}$/.test(account)) { setOther(true); setError("Add the Phone Number ID and WhatsApp Business Account ID. Both are numbers from WhatsApp Manager."); return; }
    setConnecting(true); setError("");
    const r = await fetchApi(`/api/integrations/${id}/connect-whatsapp`, { method: "post", body: { phone_number_id: phone, business_account_id: account, ...(accessToken.trim() ? { access_token: accessToken.trim() } : {}), ...rules } });
    setConnecting(false);
    if (!r.ok) {
      // TOKEN_REQUIRED: the number isn't the clinic's connected one, so Meta needs a token for it.
      if (r.code === "TOKEN_REQUIRED") setOther(true);
      setError(r.message || "Couldn't connect WhatsApp. Try again.");
      return;
    }
    navigate(`/settings/integrations/${id}`, { state: { connected: r.data?.data?.display_phone_number ? `WhatsApp ${r.data.data.display_phone_number}` : "WhatsApp" } });
  };

  const number = waba ? [waba.phone_number, waba.display_name].filter(Boolean).join(" · ") || waba.phone_number_id : null;
  const usingConnected = waba && !other;

  return <ConnectGate loading={loading} row={integration}>
    <ConnectFrame monogram="WA" title="Turn on WhatsApp lead capture" description="Create leads from Click-to-WhatsApp ad conversations.">
      <div className="mt-5 rounded-md bg-mist p-4"><p className="text-small font-semibold">Use your connected WhatsApp number</p><p className="mt-1 text-small text-muted">{waba === undefined ? <InlineLoader /> : number || wabaError || "No WhatsApp number is connected yet."}</p><Link className="mt-2 inline-block text-caption font-semibold text-sky-deep hover:underline" to="/waba">Manage WhatsApp setup</Link></div>
      <div className="mt-5 divide-y divide-line">{RULES.map((item) => <div key={item.key} className="flex items-center justify-between gap-4 py-4"><span className="text-small">{item.label}{savingRule === item.key && <span className="ml-2 text-caption text-muted">Saving…</span>}</span><Toggle checked={!!rules[item.key]} label={item.label} disabled={!!savingRule} onChange={() => setRule(item.key)} /></div>)}</div>
      <p className="text-caption text-muted">Nothing is captured until a rule is on. Rules save as you change them.</p>
      {waba && <button type="button" onClick={() => setOther(!other)} className="mt-4 text-caption font-semibold text-sky-deep hover:underline">{other ? "Use the connected number" : "Use a different number"}</button>}
      {!usingConnected && waba !== undefined && <div className="mt-3 grid gap-3 rounded-md bg-mist p-4">
        <label className="text-caption font-semibold">Phone Number ID *<TextInput inputMode="numeric" value={phoneNumberId} onChange={(e) => setPhoneNumberId(e.target.value.replace(/\D/g, ""))} placeholder="WhatsApp Manager, Phone numbers" className="mt-1" /></label>
        <label className="text-caption font-semibold">WhatsApp Business Account ID *<TextInput inputMode="numeric" value={businessAccountId} onChange={(e) => setBusinessAccountId(e.target.value.replace(/\D/g, ""))} className="mt-1" /></label>
        <label className="text-caption font-semibold">System user access token{waba && String(waba.phone_number_id) === phoneNumberId.trim() ? " (optional)" : ""}<TextInput type="password" autoComplete="off" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder="Token with whatsapp_business_management permission" className="mt-1" /><span className="mt-1 block font-normal text-muted">Not needed for the number connected under WhatsApp; Olli uses that connection's token.</span></label>
      </div>}
      <ErrorNote>{error}</ErrorNote>
      <PrimaryButton className="mt-5 w-full" loading={connecting} disabled={waba === undefined} onClick={connect}>Turn on lead capture</PrimaryButton>
    </ConnectFrame>
    {toast && <UndoToast message={toast.message} onDone={() => setToast(null)} />}
  </ConnectGate>;
}
